import store from '@/store'
import router from '@/router'

let isRedirecting = false

let inFlightApiTokenPromise = null
let inFlightGraphTokenPromise = null

export function getMsalInstance () {
  return store.state.msal?.app || null
}

export function getSelectedAccount () {
  const msal = getMsalInstance()
  if (!msal) return null

  const activeAccount = msal.getActiveAccount()
  if (activeAccount) return activeAccount

  const accounts = msal.getAllAccounts()
  if (Array.isArray(accounts) && accounts.length > 0) {
    msal.setActiveAccount(accounts[0])
    return accounts[0]
  }

  return null
}

export function getApiScope () {
  return store.state.msal?.config?.apiScope || 'api://637c2e1b-3c98-422c-9de6-8d56c7442697/access_as_user'
}

export function getGraphScopes () {
  return store.state.msal?.config?.graphScopes || ['user.read', 'user.read.all']
}

export async function getApiAccessToken ({ forceRefresh = false } = {}) {
  const msal = getMsalInstance()
  const account = getSelectedAccount()

  if (!msal || !account) {
    throw new Error('No MSAL instance or authenticated account available')
  }

  if (inFlightApiTokenPromise && !forceRefresh) {
    return inFlightApiTokenPromise
  }

  const scope = getApiScope()
  inFlightApiTokenPromise = (async () => {
    try {
      const response = await msal.acquireTokenSilent({
        account,
        scopes: [scope],
        forceRefresh
      })
      if (response && response.accessToken) {
        store.commit('SET_ACCESS', {
          accessToken: response.accessToken,
          apiAccessToken: response.accessToken,
          homeId: account.homeAccountId
        })
        return response.accessToken
      }
      throw new Error('Empty access token returned for API scope')
    } catch (err) {
      if (requiresInteraction(err)) {
        const popupResponse = await msal.acquireTokenPopup({
          account,
          scopes: [scope]
        })
        if (popupResponse && popupResponse.accessToken) {
          store.commit('SET_ACCESS', {
            accessToken: popupResponse.accessToken,
            apiAccessToken: popupResponse.accessToken,
            homeId: account.homeAccountId
          })
          return popupResponse.accessToken
        }
      }
      throw err
    } finally {
      inFlightApiTokenPromise = null
    }
  })()

  return inFlightApiTokenPromise
}

export async function getGraphAccessToken ({ forceRefresh = false } = {}) {
  const msal = getMsalInstance()
  const account = getSelectedAccount()

  if (!msal || !account) {
    throw new Error('No MSAL instance or authenticated account available')
  }

  if (inFlightGraphTokenPromise && !forceRefresh) {
    return inFlightGraphTokenPromise
  }

  const scopes = getGraphScopes()
  inFlightGraphTokenPromise = (async () => {
    try {
      const response = await msal.acquireTokenSilent({
        account,
        scopes,
        forceRefresh
      })
      return response?.accessToken || null
    } catch (err) {
      if (requiresInteraction(err)) {
        const popupResponse = await msal.acquireTokenPopup({
          account,
          scopes
        })
        return popupResponse?.accessToken || null
      }
      throw err
    } finally {
      inFlightGraphTokenPromise = null
    }
  })()

  return inFlightGraphTokenPromise
}

export function isBusinessApiRequest (url) {
  if (!url || typeof url !== 'string') return false
  if (url.includes('graph.microsoft.com')) return false
  if (url.includes('accounts.google.com') || url.includes('googleapis.com')) return false

  // Business API endpoints match backend url configurations or relative API paths
  const backendIndex = process.env.VUE_APP_BACKEND_INDEX || '/api/index.php'
  const massUploadIndex = process.env.VUE_APP_BACKEND_MASS_UPLOAD_INDEX || '/massupload/index.php'
  const downloadIndex = process.env.VUE_APP_BACKEND_DOWNLOAD_INDEX || '/download/index.php'

  if (url.includes(backendIndex) || url.includes(massUploadIndex) || url.includes(downloadIndex)) {
    return true
  }

  // Relative or gateway paths
  if (url.startsWith('/op/api/') || url.startsWith('/op-test/api/') ||
      url.startsWith('/op/massupload/') || url.startsWith('/op-test/massupload/') ||
      url.startsWith('/op/download/') || url.startsWith('/op-test/download/') ||
      url.startsWith('/list/') || url.startsWith('/op/') || url.startsWith('/massupload/')) {
    return true
  }

  return false
}

export function canReplayAfterAuthFailure (config, response) {
  if (config._retry) return false
  // Do not replay non-idempotent file uploads if request body cannot be cleanly rewound
  if (config.data instanceof FormData && config.url?.includes('/upload')) {
    return false
  }
  return true
}

export function requiresInteraction (error) {
  if (!error) return false
  const code = error.errorCode || error.message || ''
  return code.includes('consent_required') ||
    code.includes('interaction_required') ||
    code.includes('login_required')
}

export function is401Response (response) {
  if (!response) {
    return false
  }
  if (response.status === 401 || response.status === '401') {
    return true
  }
  let data = response.data
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data)
    } catch (e) {}
  }
  // Legacy business failures used a body status of 401, even with HTTP 200.
  // They must not refresh tokens or navigate away from the current editor.
  if (data && (data.message === 'Update Failed' || data.code === 'WALK_CONFIGURATION_ERROR')) return false
  if (data && (data.status === 401 || data.status === '401' || data.code === 'MISSING_TOKEN')) {
    return true
  }
  return false
}

export function redirectToLogin (explicitTargetPath = null) {
  let targetPath = explicitTargetPath
  if (!targetPath && router && router.currentRoute && router.currentRoute.path !== '/op/tables/login') {
    targetPath = router.currentRoute.fullPath
  }

  if (targetPath && targetPath !== '/' && targetPath !== '/op/tables/login') {
    try {
      sessionStorage.setItem('redirect_after_login', targetPath)
    } catch (e) {}
  }

  clearAuthViewState()

  if (
    router &&
    router.currentRoute &&
    (router.currentRoute.path === '/op/tables/login' || router.currentRoute.name === 'login')
  ) {
    return
  }

  if (isRedirecting) {
    return
  }
  isRedirecting = true

  const redirectQuery = (targetPath && targetPath !== '/' && targetPath !== '/main-page')
    ? { redirect: targetPath }
    : {}

  router.push({ path: '/op/tables/login', query: redirectQuery }).catch(() => {}).finally(() => {
    setTimeout(() => {
      isRedirecting = false
    }, 500)
  })
}

export function setupApiInterceptors (axiosInstance, options = {}) {
  if (!axiosInstance || !axiosInstance.interceptors) return
  if (axiosInstance._authInterceptorInstalled) return
  axiosInstance._authInterceptorInstalled = true
  const assumeBusiness = !!options.assumeBusiness
  const isBusinessRequest = config => !!config && (assumeBusiness || isBusinessApiRequest(config.url))

  // Request interceptor: inject business API token only to business API targets
  axiosInstance.interceptors.request.use(async config => {
    if (isBusinessRequest(config)) {
      try {
        const token = await getApiAccessToken()
        if (token) {
          config.headers = config.headers || {}
          config.headers.Authorization = `Bearer ${token}`
        }
      } catch (err) {
        // Continue request without token; backend will return 401
      }
    }
    return config
  }, error => Promise.reject(error))

  const replayAfter401 = async (originalRequest) => {
    if (!originalRequest || originalRequest._retry || !canReplayAfterAuthFailure(originalRequest)) {
      redirectToLogin()
      return null
    }
    originalRequest._retry = true
    try {
      const newToken = await getApiAccessToken({ forceRefresh: true })
      if (newToken) {
        originalRequest.headers = originalRequest.headers || {}
        originalRequest.headers.Authorization = 'Bearer ' + newToken
        return axiosInstance(originalRequest)
      }
    } catch (refreshErr) {
      console.warn('Silent token renewal failed:', refreshErr)
    }
    redirectToLogin()
    return null
  }

  // Response interceptor: controlled 401 refresh (max once), then login redirect
  axiosInstance.interceptors.response.use(
    async response => {
      if (isBusinessRequest(response.config) && is401Response(response)) {
        const replayed = await replayAfter401(response.config)
        if (replayed) return replayed
      }
      return response
    },
    async error => {
      const originalRequest = error.config
      const is401 = (error.response && is401Response(error.response)) ||
        error.status === 401 ||
        error.status === '401'

      if (isBusinessRequest(originalRequest) && is401) {
        const replayed = await replayAfter401(originalRequest)
        if (replayed) return replayed
      }

      return Promise.reject(error)
    }
  )
}

export function setupAuthInterceptor (axiosInstance) {
  return setupApiInterceptors(axiosInstance, { assumeBusiness: true })
}

export function sanitizeInternalRedirect (value) {
  if (typeof value !== 'string' || !value) {
    return '/main-page'
  }
  if (
    value.startsWith('//') ||
    value.includes('://') ||
    value.includes('/tables/login') ||
    value.includes('javascript:')
  ) {
    return '/main-page'
  }
  return value.startsWith('/') ? value : `/${value}`
}

export async function ensureAuthInitialized (timeoutMs = 5000) {
  const start = Date.now()
  while (!store.state.msal?.app) {
    if (Date.now() - start > timeoutMs) {
      break
    }
    await new Promise(resolve => setTimeout(resolve, 50))
  }
}

export function clearAuthViewState () {
  store.commit('SET_USER_EMAIL', {
    email: null,
    msId: null,
    Name: null,
    Avatar: null,
    Role: null,
    ci_Role: null,
    bases_Role: null,
    lock: null
  })
  store.commit('SET_ACCESS', {
    accessToken: null,
    apiAccessToken: null,
    graphToken: null,
    homeId: null
  })
  try {
    localStorage.clear()
  } catch (e) {
    // Ignore storage clear exceptions
  }
}

export function isSupplementalRoute (path) {
  return [
    '/op/tables/supplemental-tracker',
    '/op/tables/supplemental-expense',
    '/op/tables/tracker-access'
  ].includes(path)
}

export function hasRoutePermission (permissionState, route) {
  if (!route || !route.path) return true

  if (route.path === '/op/tables/login' || route.path === '/op/tables/notice' || route.path === '/main-page') {
    return true
  }

  // Tracker uses its own role/Tool checks and backend authorization, not Planning's lock/Role.
  if (isSupplementalRoute(route.path)) {
    return true
  }

  const role = permissionState?.Role ?? store.state.user?.Role
  const lock = permissionState?.lock ?? store.state.user?.lock

  if (lock === '1') {
    return false
  }

  if (role === '-1') {
    return true
  }

  if (route.path.includes('/maintenance')) {
    return role === '-1'
  }

  if (route.path.includes('/access/') || route.path.includes('/role')) {
    return role === '1' || role === '0'
  }

  if (role === '4' || !role) {
    return false
  }

  return true
}
